/**
 * Client-side analytics. Thin wrapper around gtag with three responsibilities:
 *  1. Inject the gtag.js script at runtime so build-time secrets (VITE_GA4_MEASUREMENT_ID,
 *     VITE_GOOGLE_ADS_ID) stay in Vite env land — no HTML templating.
 *  2. Capture the Google Ads click id (?gclid=) from the URL on first landing and persist it
 *     to a first-party cookie + ship it to the API once the user is authenticated.
 *  3. Fire SPA-side conversion + funnel events (sign_up, begin_checkout, scan_started). The
 *     authoritative purchase event is sent server-side from the Stripe webhook (see
 *     GoogleAnalyticsConversionUploader) — this client-side path is just signal for funnel
 *     analysis, not the source of truth.
 *
 * Consent Mode v2 default-deny is set in index.html. This file calls gtag('consent','update',…)
 * once the consent banner grants permission.
 */

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;

const GCLID_COOKIE = 'eaa-gclid';
const GCLID_COOKIE_DAYS = 90; // Google Ads attribution window cap.

const CONSENT_COOKIE = 'eaa-consent';
const CONSENT_COOKIE_DAYS = 365;
const CONSENT_VERSION = 1;

export type ConsentDecision = {
  analytics: boolean;
  ads: boolean;
  decidedAtIso: string;
};

type StoredConsent = { a: boolean; m: boolean; t: string; v: number };

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

let initialised = false;

/**
 * Loads the GA4 + Google Ads gtag.js and applies the matching config calls. Safe to call
 * multiple times — only the first call has effect. No-ops when the measurement id is unset
 * (dev environment without an env file).
 */
export function initAnalytics(): void {
  if (initialised) return;
  initialised = true;

  if (!GA4_ID) {
    // Dev mode without env — leave gtag as the default-deny stub from index.html so calls
    // like gtagEvent('sign_up') silently queue into dataLayer for later replay if the SPA is
    // ever loaded with real ids (e.g. on the production build).
    return;
  }

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
  document.head.appendChild(tag);

  window.gtag?.('config', GA4_ID, {
    send_page_view: true,
    // anonymize_ip is on by default in GA4 — listed here only as a reminder that we don't
    // override it. The DPA assumes IP truncation in transit.
  });

  if (GOOGLE_ADS_ID) {
    window.gtag?.('config', GOOGLE_ADS_ID, { allow_enhanced_conversions: true });
  }
}

/**
 * Reflects the user's consent choice into Consent Mode v2. Call from the consent banner's
 * accept handler. Granting analytics_storage backfills any events queued under default-deny.
 */
export function setConsent(opts: {
  analytics: boolean;
  ads: boolean;
}): void {
  window.gtag?.('consent', 'update', {
    analytics_storage: opts.analytics ? 'granted' : 'denied',
    ad_storage: opts.ads ? 'granted' : 'denied',
    ad_user_data: opts.ads ? 'granted' : 'denied',
    ad_personalization: opts.ads ? 'granted' : 'denied',
  });
}

/**
 * Captures the GCLID from the current URL (if present) into a first-party cookie. Idempotent;
 * an existing cookie wins so a returning visitor's later ad click doesn't overwrite the first-
 * touch attribution. Returns the GCLID currently in scope (newly captured or existing).
 */
export function captureGclidFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get('gclid') || '').trim();
    if (fromUrl && /^[A-Za-z0-9_-]{1,200}$/.test(fromUrl)) {
      if (!readGclidCookie()) {
        writeGclidCookie(fromUrl);
      }
    }
  } catch {
    /* swallow — analytics never breaks the app */
  }
  return readGclidCookie();
}

/**
 * Reads the captured GCLID without consulting the URL. Used by checkout flows so the GCLID
 * survives across pages.
 */
export function getStoredGclid(): string | null {
  return readGclidCookie();
}

function readGclidCookie(): string | null {
  const match = document.cookie
    .split('; ')
    .find(c => c.startsWith(`${GCLID_COOKIE}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.split('=')[1] ?? '');
  return /^[A-Za-z0-9_-]{1,200}$/.test(value) ? value : null;
}

function writeGclidCookie(gclid: string): void {
  const expires = new Date(Date.now() + GCLID_COOKIE_DAYS * 86400_000).toUTCString();
  // SameSite=Lax so the cookie survives the Stripe checkout redirect round-trip.
  document.cookie = `${GCLID_COOKIE}=${encodeURIComponent(gclid)}; Path=/; Max-Age=${GCLID_COOKIE_DAYS * 86400}; Expires=${expires}; SameSite=Lax`;
}

/**
 * Reads the user's prior consent decision from cookie. Returns null if never decided or if
 * the stored version is older than CONSENT_VERSION (which triggers a re-prompt when the
 * banner UI mounts).
 */
export function getStoredConsent(): ConsentDecision | null {
  const match = document.cookie
    .split('; ')
    .find(c => c.startsWith(`${CONSENT_COOKIE}=`));
  if (!match) return null;
  try {
    const raw = decodeURIComponent(match.split('=')[1] ?? '');
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.v !== CONSENT_VERSION) return null;
    return { analytics: !!parsed.a, ads: !!parsed.m, decidedAtIso: parsed.t };
  } catch {
    return null;
  }
}

/**
 * Persists the user's consent decision AND applies it to gtag's Consent Mode v2 state in
 * the same call. Use this from the banner's Accept / Reject buttons.
 */
export function recordConsentDecision(opts: { analytics: boolean; ads: boolean }): void {
  const stored: StoredConsent = {
    a: opts.analytics,
    m: opts.ads,
    t: new Date().toISOString(),
    v: CONSENT_VERSION,
  };
  const expires = new Date(Date.now() + CONSENT_COOKIE_DAYS * 86400_000).toUTCString();
  const value = encodeURIComponent(JSON.stringify(stored));
  document.cookie = `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=${CONSENT_COOKIE_DAYS * 86400}; Expires=${expires}; SameSite=Lax`;
  setConsent(opts);
}

/**
 * Reads the stored decision and applies it to gtag synchronously. Call from main.tsx
 * BEFORE initAnalytics() so the first auto page_view fires with the right consent state
 * for returning visitors who already accepted.
 */
export function applyStoredConsent(): void {
  const decision = getStoredConsent();
  if (decision) {
    setConsent({ analytics: decision.analytics, ads: decision.ads });
  }
}

/**
 * Clears the stored decision. The banner re-renders on next mount and the user is
 * re-prompted. Used by the "Cookie settings" footer link via a custom event.
 */
export function clearStoredConsent(): void {
  document.cookie = `${CONSENT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Generic event helper. The events we emit map to GA4's recommended event names so they
 * appear correctly in standard reports without a custom registration.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  window.gtag?.('event', name, params);
}

/**
 * SPA-side conversion signal. The authoritative one is server-side; this fires on the
 * BillingSuccessPage so client-side conversions linked from Google Ads also get credit.
 * Safe to call before the server-side path has completed — GA4 de-dupes by transaction_id.
 */
export function trackPurchase(opts: {
  transactionId: string;
  value: number;
  currency: string;
  planCode: string;
}): void {
  trackEvent('purchase', {
    transaction_id: opts.transactionId,
    value: opts.value,
    currency: opts.currency,
    items: [
      {
        item_id: opts.planCode,
        item_name: opts.planCode,
        price: opts.value,
        quantity: 1,
      },
    ],
  });

  // Google Ads conversion (client-side companion to the server-side OfflineConversionImport).
  // The conversion label is configured in the Ads UI and injected via VITE_GOOGLE_ADS_PURCHASE_LABEL,
  // e.g. send_to: 'AW-1234567890/AbC-D_efGh12345'.
  const sendTo = import.meta.env.VITE_GOOGLE_ADS_PURCHASE_LABEL as string | undefined;
  if (sendTo) {
    trackEvent('conversion', {
      send_to: sendTo,
      transaction_id: opts.transactionId,
      value: opts.value,
      currency: opts.currency,
    });
  }
}
